import { create } from "zustand";
import toast from "react-hot-toast";
import { axiosInstance } from "../lib/axios";
import { getSocket } from "../socket";
import { useAuthStore } from "./useAuthStore";

// Keep references to ONLY this store's socket handlers.
let chatNewMessageHandler = null;
let chatMessageSeenHandler = null;

// Blocks double click / double Enter while a send is in progress.
let isSendingLock = false;

// Only the latest message-fetch request is allowed to update state.
let messagesRequestId = 0;

/* ---------- helpers ---------- */
const ANNOUNCEMENT_PREFIX = "group:announcements";
const makeAnnId = (company) => `${ANNOUNCEMENT_PREFIX}:${company}`;
const isAnnouncementId = (id = "") =>
  String(id).startsWith(`${ANNOUNCEMENT_PREFIX}:`);
const companyFromAnnId = (id = "") => String(id).split(":").pop();
const BOTH_COMPANIES = ["SVB"];

const canon = (s) => String(s || "").trim().toUpperCase();

// Unique id per send, so the server can ignore a repeated request.
const makeClientId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

// Add a message only if its _id is not already present.
const appendUnique = (list = [], msg) => {
  if (!msg) return list;
  const id = String(msg._id || "");
  if (id && list.some((m) => String(m?._id) === id)) return list;
  return [...list, msg];
};

// Remove duplicate _ids from a list (used after fetching from server).
const dedupeById = (list = []) => {
  const seen = new Set();
  return (list || []).filter((m) => {
    const id = String(m?._id || "");
    if (!id) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

/** Keep category fields (category/name/unread/lastAt). Uppercase the key. */
const toCat = (c) => {
  if (!c) return null;

  if (typeof c === "string") {
    const s = c.trim();
    if (!s) return null;
    return { category: canon(s), name: s, unread: 0, lastAt: null };
  }

  if (typeof c === "object") {
    const categoryRaw = String(
      c.category ?? c.code ?? c.key ?? c.slug ?? c.name ?? c._id ?? ""
    ).trim();
    if (!categoryRaw) return null;

    const category = canon(categoryRaw);
    const name = String(c.name ?? c.displayName ?? categoryRaw).trim();
    const unread = Number(c.unread ?? 0);
    const lastAt = c.lastAt ?? null;
    return { category, name, unread, lastAt };
  }

  return null;
};

/** Merge list by category; combine unread + keep newest lastAt + best name */
const uniqByCategory = (arr) => {
  const map = new Map();
  for (const it of arr) {
    if (!it || !it.category) continue;
    const key = it.category;
    if (!map.has(key)) {
      map.set(key, { ...it });
    } else {
      const prev = map.get(key);
      map.set(key, {
        category: key,
        name: prev.name?.length >= it.name?.length ? prev.name : it.name,
        unread: Number(prev.unread || 0) + Number(it.unread || 0),
        lastAt:
          new Date(it.lastAt || 0).getTime() >
          new Date(prev.lastAt || 0).getTime()
            ? it.lastAt
            : prev.lastAt,
      });
    }
  }
  return Array.from(map.values());
};

/* ======================================================
 🧠 Zustand Chat Store
====================================================== */
export const useChatStore = create((set, get) => ({
  /* ----- DM core ----- */
  messages: [],
  users: [],
  selectedUser: null,
  isUsersLoading: false,
  isMessagesLoading: false,
  unreadMap: {},

  /* ----- Category state ----- */
  categories: [],
  selectedCategory: null,
  categoryMessages: [],
  categoryUnreadMap: {},
  isCategoryLoading: false,
  categoryUsers: [],

  /* ===== REAL-TIME SIDEBAR UPDATERS ===== */
  updateLastMessage: (threadId, text) =>
    set((state) => ({
      users: (state.users || []).map((u) =>
        u._id === threadId ? { ...u, lastMessage: text } : u
      ),
    })),

  incrementUnread: (threadId) =>
    set((state) => {
      const curr = Number(state.unreadMap?.[threadId] || 0);
      return {
        unreadMap: { ...(state.unreadMap || {}), [threadId]: curr + 1 },
      };
    }),

  /* ---------------- DM LIST ---------------- */
  getUsers: async () => {
    set({ isUsersLoading: true });
    try {
      const res = await axiosInstance.get("/messages/users");
      set({ users: res.data || [] });
    } catch {
      toast.error("Failed to load users");
    } finally {
      set({ isUsersLoading: false });
    }
  },

  /* --------------- MESSAGES / PROJECT / ANNOUNCEMENTS --------------- */
  getMessages: async (userId) => {
    if (isAnnouncementId(userId)) {
      const company = companyFromAnnId(userId);
      return get().getAnnouncements(company);
    }

    const reqId = ++messagesRequestId;

    // Same chat already on screen -> refresh silently (no skeleton flash).
    const alreadyShowing =
      String(get().selectedUser?._id) === String(userId) &&
      (get().messages || []).length > 0;

    if (!alreadyShowing) set({ isMessagesLoading: true });

    try {
      const selectedType = get().selectedUser?.type;
      let url = `/messages/${userId}`;
      if (selectedType === "project")
        url = `/project-messages/${userId}/messages`;

      const res = await axiosInstance.get(url);

      // A newer request started, or user switched chat -> ignore this response.
      if (reqId !== messagesRequestId) return;
      if (String(get().selectedUser?._id) !== String(userId)) return;

      const fetched = dedupeById(res.data || []);
      const fetchedIds = new Set(fetched.map((m) => String(m._id)));
      const lastFetchedTime = fetched.length
        ? new Date(fetched[fetched.length - 1].createdAt).getTime()
        : 0;

      // Keep only socket messages that belong to THIS chat.
      const belongsToThisChat = (m) => {
        const s = String(m?.senderId?._id || m?.senderId || "");
        const r = String(m?.receiverId?._id || m?.receiverId || "");
        return s === String(userId) || r === String(userId);
      };

      const newer = (get().messages || []).filter(
        (m) =>
          m?._id &&
          !fetchedIds.has(String(m._id)) &&
          belongsToThisChat(m) &&
          new Date(m.createdAt).getTime() > lastFetchedTime
      );

      set({ messages: [...fetched, ...newer] });
    } catch {
      if (reqId === messagesRequestId) toast.error("Failed to load messages");
    } finally {
      // Only the latest request is allowed to turn the loader off.
      if (reqId === messagesRequestId) set({ isMessagesLoading: false });
    }
  },

  /* ----- Announcements ----- */
  getAnnouncements: async (companyArg) => {
    const reqId = ++messagesRequestId;
    set({ isMessagesLoading: true });
    try {
      const company =
        companyArg ||
        (isAnnouncementId(get().selectedUser?._id)
          ? companyFromAnnId(get().selectedUser._id)
          : "SVB");

      const res = await axiosInstance.get("/announcements/messages", {
        params: { company },
      });

      // Newer request started (user switched chat) -> ignore this response.
      if (reqId !== messagesRequestId) return;

      set({ messages: dedupeById(res.data || []) });

      // mark as read FIRST, then refresh counts (order matters)
      await get().markAnnouncementsRead(company);
      await get().fetchAnnouncementUnreadCount(company);
    } catch {
      if (reqId === messagesRequestId)
        toast.error("Failed to load announcements");
    } finally {
      if (reqId === messagesRequestId) set({ isMessagesLoading: false });
    }
  },

  postAnnouncement: async (formData, companyArg) => {
    try {
      const company =
        companyArg ||
        (isAnnouncementId(get().selectedUser?._id)
          ? companyFromAnnId(get().selectedUser._id)
          : "SVB");

      if (
        formData &&
        typeof formData.get === "function" &&
        !formData.get("company")
      ) {
        formData.append("company", company);
      }

      const res = await axiosInstance.post("/announcements", formData, {
        params: { company },
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (get().selectedUser?._id === makeAnnId(company)) {
        set((s) => ({ messages: appendUnique(s.messages, res.data) }));
        await get().markAnnouncementsRead(company);
      }
      await get().fetchAnnouncementUnreadCount(company);
      return res.data;
    } catch (err) {
      toast.error("Failed to post announcement");
      throw err;
    }
  },

  markAnnouncementsRead: async (companyArg) => {
    const companies = companyArg
      ? [companyArg]
      : isAnnouncementId(get().selectedUser?._id)
      ? [companyFromAnnId(get().selectedUser._id)]
      : BOTH_COMPANIES;

    try {
      await Promise.all(
        companies.map((c) =>
          // company goes in BOTH body and query, so the backend finds it
          axiosInstance.post(
            "/announcements/read",
            { company: c },
            { params: { company: c } }
          )
        )
      );
      set((s) => {
        const next = { ...(s.unreadMap || {}) };
        companies.forEach((c) => (next[makeAnnId(c)] = 0));
        return { unreadMap: next };
      });
    } catch {}
  },

  fetchAnnouncementUnreadCount: async (companyArg) => {
    const companies = companyArg ? [companyArg] : BOTH_COMPANIES;
    try {
      const results = await Promise.all(
        companies.map((c) =>
          axiosInstance
            .get("/announcements/unread-count", { params: { company: c } })
            .then((r) => ({ c, unread: Number(r.data?.unread || 0) }))
            .catch(() => ({ c, unread: 0 }))
        )
      );
      set((s) => {
        const next = { ...(s.unreadMap || {}) };
        const openId = String(s.selectedUser?._id || "");
        results.forEach(({ c, unread }) => {
          const id = makeAnnId(c);
          // the announcement thread that is open right now is always "read"
          next[id] = openId === id ? 0 : unread;
        });
        return { unreadMap: next };
      });
    } catch {}
  },

  /* ----- Generic send (DM/Project/Announcements) ----- */
  sendMessage: async (formData, customTargetUserId = null) => {
    // ⭐ block double click / double Enter
    if (isSendingLock) return;
    isSendingLock = true;

    const { selectedUser } = get();
    const targetUserId = customTargetUserId || selectedUser?._id;
    const targetType = selectedUser?.type || "user";

    const toAnnouncements =
      (customTargetUserId && isAnnouncementId(customTargetUserId)) ||
      (selectedUser && isAnnouncementId(selectedUser._id));

    if (!targetUserId && !toAnnouncements) {
      isSendingLock = false;
      toast.error("Please select a user to send message");
      return;
    }

    try {
      let res;

      if (toAnnouncements) {
        const company = customTargetUserId
          ? companyFromAnnId(customTargetUserId)
          : companyFromAnnId(selectedUser._id);

        res = await axiosInstance.post("/announcements", formData, {
          params: { company },
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (get().selectedUser?._id === makeAnnId(company)) {
          set((s) => ({ messages: appendUnique(s.messages, res.data) }));
          await get().markAnnouncementsRead(company);
        }
        await get().fetchAnnouncementUnreadCount(company);
      } else if (targetType === "project") {
        res = await axiosInstance.post(
          `/project-messages/${targetUserId}/messages`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        if (get().selectedUser?._id === targetUserId) {
          set((s) => ({ messages: appendUnique(s.messages, res.data) }));
        }
      } else {
        // ⭐ unique id for this send (server uses it to block duplicates)
        if (formData?.get && !formData.get("clientId")) {
          formData.append("clientId", makeClientId());
        }

        res = await axiosInstance.post(
          `/messages/send/${targetUserId}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        if (get().selectedUser?._id === targetUserId) {
          set((s) => ({ messages: appendUnique(s.messages, res.data) }));
        }

        // Backend already emits "newMessage" to the receiver after saving,
        // so the client does not emit "send:message" again.

        let updatedUsers = get().users.map((user) =>
          user._id === targetUserId
            ? {
                ...user,
                lastMessage: res.data.content || res.data.text || "Attachment",
                lastMessageTime: new Date(
                  res.data.createdAt
                ).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
              }
            : user
        );
        const moved = updatedUsers.find((u) => u._id === targetUserId);
        updatedUsers = updatedUsers.filter((u) => u._id !== targetUserId);
        if (moved) updatedUsers.unshift(moved);
        set({ users: updatedUsers });
      }
    } catch {
      toast.error("Failed to send message");
    } finally {
      isSendingLock = false; // ⭐ always release
    }
  },

  /* ---------------- LIVE SOCKET ---------------- */
  subscribeToMessages: () => {
    const socket = getSocket();
    if (!socket) return;

    if (!chatNewMessageHandler) {
      chatNewMessageHandler = (newMessage) => {
        const myUserId = String(useAuthStore.getState()?.authUser?._id || "");

        const senderId = String(
          newMessage?.senderId?._id || newMessage?.senderId || ""
        );
        const receiverId = String(
          newMessage?.receiverId?._id || newMessage?.receiverId || ""
        );

        /* ---- CATEGORY MENTION CHECK ---- */
        if (
          newMessage?.scope === "category" &&
          Array.isArray(newMessage?.mentions) &&
          newMessage.mentions.some((m) => String(m?._id || m) === myUserId)
        ) {
          toast.success("You were mentioned in a category!");
        }

        const { selectedUser, users, unreadMap } = get();

        /* ---- ANNOUNCEMENTS ---- */
        const rcvId = receiverId;
        const isAnn =
          rcvId.startsWith("group:announcements") ||
          newMessage?.scope === "announcement";

        if (isAnn) {
          const annId = rcvId;

          if (selectedUser && String(selectedUser._id) === annId) {
            set((s) => ({
              messages: appendUnique(s.messages, newMessage),
              unreadMap: { ...(s.unreadMap || {}), [annId]: 0 },
            }));
          } else {
            const prev = Number(unreadMap?.[annId] || 0);
            set({
              unreadMap: { ...(unreadMap || {}), [annId]: prev + 1 },
            });
          }
          return;
        }

        /* ---- NORMAL DM / PROJECT ---- */
        let threadId = senderId;
        if (senderId === myUserId) threadId = receiverId;

        const selectedId = String(selectedUser?._id || "");
        const isCurrentThread =
          !!selectedUser &&
          (threadId === selectedId ||
            senderId === selectedId ||
            receiverId === selectedId);

        // ⭐ functional update: dedupe against LATEST state by _id
        if (isCurrentThread) {
          set((s) => ({ messages: appendUnique(s.messages, newMessage) }));
        }

        const preview =
          newMessage?.content ||
          newMessage?.text ||
          newMessage?.fileName ||
          "Attachment";

        /* -- UPDATE USERS[] LIST -- */
        let updatedUsers = (users || []).map((u) => {
          if (String(u._id) === threadId) {
            return {
              ...u,
              lastMessage: preview,
              unreadCount:
                isCurrentThread || senderId === myUserId
                  ? 0
                  : (u.unreadCount || 0) + 1,
            };
          }
          return u;
        });

        const moved = updatedUsers.find((u) => String(u._id) === threadId);
        updatedUsers = updatedUsers.filter((u) => String(u._id) !== threadId);
        if (moved) updatedUsers.unshift(moved);

        /* -- UPDATE unreadMap -- */
        const nextUnread = { ...(unreadMap || {}) };
        if (senderId === myUserId || isCurrentThread) {
          nextUnread[threadId] = 0;
        } else {
          nextUnread[threadId] = (nextUnread[threadId] || 0) + 1;
        }

        set({ users: updatedUsers, unreadMap: nextUnread });
      };

      socket.on("newMessage", chatNewMessageHandler);
    }

    if (!chatMessageSeenHandler) {
      chatMessageSeenHandler = (messageId) => {
        set((s) => ({
          messages: (s.messages || []).map((m) =>
            String(m?._id) === String(messageId) ? { ...m, isRead: true } : m
          ),
        }));
      };

      socket.on("messageSeen", chatMessageSeenHandler);
    }
  },

  unsubscribeFromMessages: () => {
    const socket = getSocket();
    if (!socket) return;

    if (chatNewMessageHandler) {
      socket.off("newMessage", chatNewMessageHandler);
      chatNewMessageHandler = null;
    }

    if (chatMessageSeenHandler) {
      socket.off("messageSeen", chatMessageSeenHandler);
      chatMessageSeenHandler = null;
    }
  },

  /* ---------------- STATE ---------------- */
  setSelectedUser: (selectedUser) =>
    set((state) => {
      const next = { ...state.unreadMap };
      if (selectedUser?._id) next[selectedUser._id] = 0;

      const changed =
        String(state.selectedUser?._id || "") !==
        String(selectedUser?._id || "");

      return {
        selectedUser,
        unreadMap: next,
        // clear old chat messages when switching to a different chat
        ...(changed ? { messages: [] } : {}),
      };
    }),

  /* ---------------- CATEGORY ACTIONS ---------------- */
  getCategoryList: async () => {
    try {
      const r = await axiosInstance.get("/categories/with-unread");
      const normalized = uniqByCategory(
        (Array.isArray(r.data) ? r.data : []).map(toCat).filter(Boolean)
      );
      set({ categories: normalized });
    } catch (e) {
      toast.error("Failed to load categories");
    }
  },

  fetchCategoryUsers: async (categoryKey) => {
    try {
      const cat = canon(categoryKey);
      if (!cat) return;

      const res = await axiosInstance.get(
        `/categories/${encodeURIComponent(cat)}/users`
      );

      set({ categoryUsers: Array.isArray(res.data) ? res.data : [] });
    } catch (err) {
      console.error("Failed to fetch category users", err);
      set({ categoryUsers: [] });
    }
  },

  setSelectedCategory: async (catOrKey) => {
    const obj = toCat(catOrKey);
    const key =
      obj?.category || (typeof catOrKey === "string" ? canon(catOrKey) : null);

    set((s) => {
      const nextUnread = { ...(s.categoryUnreadMap || {}) };
      if (key) nextUnread[key] = 0;

      const nextCats = (s.categories || []).map((c) =>
        c.category === key ? { ...c, unread: 0 } : c
      );

      return {
        selectedCategory: key || null,
        categoryUnreadMap: nextUnread,
        categories: nextCats,
      };
    });

    if (key) {
      await get().loadCategoryMessages(key);
      await get().fetchCategoryUsers(key);
      try {
        await axiosInstance.post(
          `/category-messages/category/${encodeURIComponent(key)}/mark-seen`
        );
      } catch {}
    } else {
      set({ categoryMessages: [] });
    }
  },

  loadCategoryMessages: async (categoryKey) => {
    if (!categoryKey) return;
    set({ isCategoryLoading: true });
    try {
      const res = await axiosInstance.get(
        `/category-messages/category/${encodeURIComponent(categoryKey)}`
      );
      set({
        categoryMessages: Array.isArray(res.data) ? dedupeById(res.data) : [],
      });
    } catch (e) {
      toast.error("Failed to load category messages");
    } finally {
      set({ isCategoryLoading: false });
    }
  },

  sendCategoryMessage: async (formData, categoryKey) => {
    try {
      const cat = canon(categoryKey || get().selectedCategory);
      if (!cat) {
        toast.error("Select a category first");
        return;
      }
      const res = await axiosInstance.post(
        `/category-messages/category/${encodeURIComponent(cat)}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (canon(get().selectedCategory) === cat) {
        set((s) => ({
          categoryMessages: appendUnique(s.categoryMessages, res.data),
        }));
      }
      get().getCategoryList();
      return res.data;
    } catch (e) {
      toast.error("Failed to send message");
      throw e;
    }
  },

  deleteCategoryMessage: async (messageId, categoryKey) => {
    try {
      const cat = canon(categoryKey || get().selectedCategory);
      if (!cat || !messageId) return;

      await axiosInstance.delete(
        `/category-messages/category/${encodeURIComponent(
          cat
        )}/message/${encodeURIComponent(messageId)}`
      );

      if (canon(get().selectedCategory) === cat) {
        set((s) => ({
          categoryMessages: s.categoryMessages.filter(
            (m) => m._id !== messageId
          ),
        }));
      }
      get().getCategoryList();
    } catch (e) {
      toast.error("Failed to delete message");
      throw e;
    }
  },

  /* ---------------- UNREAD MANAGEMENT ---------------- */
  clearUnreadCount: (userId) => {
    if (!userId) return;
    set((state) => {
      const next = { ...(state.unreadMap || {}) };
      next[userId] = 0;
      return { unreadMap: next };
    });
  },

  markUserAsRead: (userId) => {
    if (!userId) return;
    set((state) => ({
      users: (state.users || []).map((u) =>
        u._id === userId ? { ...u, unreadCount: 0 } : u
      ),
    }));
  },
}));